/**
 * "What moved since I last looked" for the staff invoice console.
 *
 * The creator side has this already — the API returns `has_unseen_update` and
 * the badge clears through /mine/seen. There is no staff equivalent, so this
 * derives it in the browser: remember each invoice's status, and flag the ones
 * whose status differs on the next visit.
 *
 * That makes it per-browser, not per-user: it will not follow someone to
 * another machine, and clearing site data resets it. Good enough to stop staff
 * hunting for what changed; not a substitute for the server-side flag, which
 * is the right long-term home for this.
 */

export const STATUS_WATCH_KEY = 'invoice_console_seen_statuses';

/** `{ invoiceId: status }` for the invoices currently on screen. */
export function snapshotOf(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.reduce((acc, row) => {
    if (row && row.id !== undefined && row.id !== null) {
      return { ...acc, [row.id]: row.status };
    }
    return acc;
  }, {});
}

/**
 * Ids whose status differs from the remembered snapshot, plus ones that are
 * new. An empty snapshot means a first visit, and flags nothing — lighting up
 * every row the first time would train people to ignore the badge.
 */
export function changedIds(rows, seen) {
  const snapshot = seen && typeof seen === 'object' ? seen : {};
  if (Object.keys(snapshot).length === 0) return [];

  const list = Array.isArray(rows) ? rows : [];
  return list
    .filter((row) => {
      if (!row || row.id === undefined || row.id === null) return false;
      // Object keys are strings once a snapshot has been through JSON.
      const previous = snapshot[String(row.id)];
      return previous !== undefined && previous !== row.status;
    })
    .map((row) => row.id)
    .concat(
      list
        .filter(
          (row) =>
            row && row.id !== undefined && row.id !== null && snapshot[String(row.id)] === undefined,
        )
        .map((row) => row.id),
    );
}

/** The stored snapshot updated with what is on screen now; never mutates. */
export function mergeSnapshot(seen, rows) {
  const snapshot = seen && typeof seen === 'object' ? seen : {};
  return { ...snapshot, ...snapshotOf(rows) };
}

/** Read a snapshot, tolerating absent, blocked or corrupt storage. */
export function readSnapshot(storage, key) {
  try {
    const raw = storage ? storage.getItem(key) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

/** Write a snapshot. Storage can be unavailable; that is not worth an error. */
export function writeSnapshot(storage, key, snapshot) {
  try {
    if (storage) storage.setItem(key, JSON.stringify(snapshot || {}));
  } catch (e) {
    /* private browsing, quota, or no storage at all — the badge just resets */
  }
}
